import { AfterViewInit, ChangeDetectorRef, Component } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import { IUserCredential } from "app/auth/user.model";
import { IonicPage, NavParams } from "ionic-angular";
import { NavigationService } from "navigation/navigation.service";
import { commonVar } from "providers/common-var/common";

@IonicPage({
  name: "reset-password",
  segment: "reset-password",
})
@Component({
  selector: "reset-password",
  templateUrl: "./reset-password.html",
})
export class ResetPassword implements AfterViewInit {
  emailRegex = commonVar.EMAIL_REGEX;
  loading: boolean;
  user: IUserCredential;
  errorMessage: String;
  errorEmail: String;
  showPassword: boolean;
  showConfirmPassword: boolean;
  showForgotMessage: boolean;

  constructor(
    public authSvc: AuthService,
    public navSvc: NavigationService,
    public navParams: NavParams,
    private cdr: ChangeDetectorRef
  ) {
    this.loading = false;
    this.user = {
      password: "",
      confirmPassword: "",
    } as IUserCredential;
  }
  validateInput(event: any): void {
    let value = event.target.value;
    let cleanedValue = value.replace(/[^0-9]/g, "");
    event.target.value = cleanedValue;
    this.user.code = cleanedValue;
  }

  ngAfterViewInit() {
    this.user.email = this.navParams.get("email");
    this.showForgotMessage = this.navParams.get("showForgotMessage");
    this.cdr.detectChanges();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  public backLogin(): void {
    this.navSvc.goToInternalPage("sign-in-page", {});
  }

  public clearErrorIfMatch(): void {
    // CO-3348: the two trailing branches this replaces were provably dead —
    // `this.user.password && this.user.password.length === 0` requires the
    // string to be both truthy and empty, which no value satisfies (same for
    // confirmPassword). Their evident intent was "clearing a field clears the
    // mismatch error", and that never happened: once "Passwords do not match"
    // appeared, emptying a box left it on screen until the other box was edited
    // into a match. Implemented properly below.
    if (!this.user.password || !this.user.confirmPassword) {
      // Nothing to compare yet — drop any stale mismatch message.
      this.errorMessage = "";
      return;
    }
    this.errorMessage =
      this.user.password === this.user.confirmPassword
        ? ""
        : "Passwords do not match";
  }

  public confirmForgotPassword(): void {
    // Start loading
    this.loading = true;

    // CO-3348 (round 2): clear the previous attempt's errors before starting a
    // new one. This is the SAME defect already fixed in first-time-login.ts and
    // forgot-password.ts, and it was missed the first time because those two are
    // copy-paste twins of each other and this screen is not — so "fix both
    // copies" did not reach it. Symptom here: mistype the verification code, read
    // "Invalid verification code…", correct it and resubmit, and the stale
    // message stays on screen for the whole round trip. If the retry then fails
    // for a DIFFERENT reason there is a window where the wrong reason is the only
    // thing displayed.
    this.errorMessage = "";
    this.errorEmail = "";

    // Check if any field is empty
    if (
      !this.user.email ||
      !this.user.code ||
      !this.user.password ||
      !this.user.confirmPassword
    ) {
      this.errorMessage = "Please fill in all fields";
      this.loading = false;
      return;
    }

    // Check if the email is valid
    if (!this.isEmailValid(this.user.email)) {
      this.errorEmail = "Please enter a valid email";
      this.loading = false;
      return;
    }

    // Check if passwords match
    if (this.user.password !== this.user.confirmPassword) {
      this.errorMessage = "Passwords do not match";
      this.loading = false;
      return;
    }
    // Check password complexity rules
    if (!this.validatePassword(this.user.password, this.user.email)) {
      this.errorMessage = "Password does not meet complexity requirements";
      this.loading = false;
      return;
    }
    // Confirm password reset
    this.authSvc
      .confirmForgotPassword(
        this.user.email,
        this.user.code,
        this.user.password
      )
      .then(() => {
        // Navigate to sign-in page after successful password reset
        this.navSvc.goToInternalPage("sign-in-page", {
          showSuccessMessage: true,
        });
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        if (error.code === "CodeMismatchException") {
          this.errorMessage =
            "Invalid verification code. Please check your email and try again.";
        } else if (error.code === "ExpiredCodeException") {
          this.errorMessage =
            "The verification code has expired. Please request a new one.";
        } else if (error.code === "LimitExceededException") {
          this.errorMessage =
            "You have exceeded the limit for password reset attempts. Please try again later.";
        } else {
          this.errorMessage =
            "An error occurred while processing your request. Please try again later.";
        }
      });
  }

  isEmailValid(email: string): boolean {
    return this.emailRegex.test(email);
  }

  validatePassword(
    password: string,
    userName: string,
    fullName?: string
  ): boolean {
    // Rule 1: Minimum length of 8 characters
    if (password.length < 8) {
      return false;
    }

    // Rule 3: Must use characters from ALL FOUR categories below.
    //
    // CO-3348 — the comment here used to read "at least 3 of the 4", which the
    // code has never done: the check is `< 4`, so all four are required. The
    // on-screen criteria (reset-password.html) also list all four, so the CODE
    // and the SCREEN agree and the comment was the outlier. Corrected rather
    // than loosened: relaxing to 3 would weaken the password policy, which is
    // not a call a coverage ticket gets to make. If 3-of-4 was the intended
    // policy, that is a product decision and needs its own ticket.
    //
    // The special-character class below is deliberately NARROWER than what
    // Cognito itself accepts (Cognito also allows ( ) [ ] : ; , < > \ " ` $).
    // A user who picks one of those is told their password "does not meet
    // complexity requirements" even though Cognito would have taken it. That is
    // pre-existing and left alone here — but `=` was a genuine defect: the
    // on-screen list advertises `=` and the class omitted it, so following the
    // instructions on the page produced a rejection. `=` is now accepted.
    const regexCategories = [
      /[A-Z]/, // Uppercase
      /[a-z]/, // Lowercase
      /[0-9]/, // Digits
      /[~!@#$%^*&?.+_'/{|}=-]/, // Special characters (`=` added — CO-3348)
    ];
    let categoryCount = 0;
    for (const regexCategory of regexCategories) {
      if (regexCategory.test(password)) {
        categoryCount++;
      }
    }
    if (categoryCount < 4) {
      return false;
    }

    // Rule 4: Must not use control characters and other non-printing characters
    const regexControlCharacters = /[\x00-\x1F\x7F]/; // Control and non-printing characters
    if (regexControlCharacters.test(password)) {
      return false;
    }

    // Rule 5: Must not contain any spaces
    const regexSpaces = /\s/;
    if (regexSpaces.test(password)) {
      return false;
    }

    return true; // The password meets all the rules
  }
}



// WEBPACK FOOTER //
// ./src/pages/common/cognito/reset-password-cognito/reset-password.ts